import * as React from 'react';
import { connect } from 'react-redux';
import { isEqual, isNil } from 'lodash';

import ManagedByTag from 'components/managed-by-tag/ManagedByTag';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import MoreButton from 'components/buttons/more-button/MoreButton';
import CreatorTilePriceReco from 'components/creator-tile/price-reco/CreatorTilePriceReco';
import MessengerSubcampaignState from 'modules/messenger/components/MessengerSubcampaignState';
import DeliverableTypeCounters from 'modules/deliverables/components/deliverable-type-counters/DeliverableTypeCounters';
import HeaderSeparator from 'modules/messenger/components/header-separator/HeaderSeparator';
import { MetricsChannelsBasicItem } from 'components/metrics/channels-basic/types';
import MetricsChannelsBasicSingleEntry from 'components/metrics/channels-basic/MetricsChannelsBasicSingleEntry';

import { navigate } from 'libs/router';

import { markConversationAsUnread } from 'modules/messenger/reducers/conversations';
import { fetchUnreadConversations } from 'modules/messenger/reducers/messengerNotifications';

import { isDisableUnreadMessagesSelector } from 'selectors/disableUnreadMessages';

import { __t, applyTranslations } from 'utils/language';
import { getEntityFromProfile, getFullName } from 'utils/view';
import { getManagedByBrand } from 'utils/profile';
import { ORGANIZATION_PROFILE_TYPE_BRAND } from 'utils/organization';

import type { BrandProfile, ChannelTypes, Deliverable, OrganizationProfileType, Subcampaign } from 'types';

applyTranslations(require('./messenger-messages-header.i18n.json'));

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: false
});

type OwnProps = {
  profileType: OrganizationProfileType,
  subcampaignId: number | undefined | null,
  variablePaymentsAmount: number
};

type StateProps = {
  brandProfile: BrandProfile,
  subcampaign: Subcampaign,
  offerDeliverables: Array<Deliverable>,
  currentOrganizationId: number | undefined | null,
  disableUnreadMessages: boolean
};

type DispatchProps = {
  dispatchFetchUnreadConversations: (a: number) => Promise<void>,
  dispatchMarkConversationAsUnread: (a: string) => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

class MessengerMessagesHeader extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    const { subcampaign, offerDeliverables } = this.props;

    return !isEqual(subcampaign, nextProps.subcampaign)
      || !isEqual(offerDeliverables, nextProps.offerDeliverables);
  }

  handleMarkConversationAsUnread = (): void => {
    const {
      currentOrganizationId,
      disableUnreadMessages,
      subcampaign,
      dispatchMarkConversationAsUnread,
      dispatchFetchUnreadConversations
    } = this.props;

    navigate.to(`/campaigns/${subcampaign.campaign.id}/details/${subcampaign.id}`);

    dispatchMarkConversationAsUnread(subcampaign.conversationExternalId)
      .then(() => {
        if (!disableUnreadMessages && currentOrganizationId) {
          dispatchFetchUnreadConversations(currentOrganizationId);
        }
      });
  };

  private renderChannels(): React.ReactNode {
    const { subcampaign } = this.props;

    const shownChannels: Array<ChannelTypes> = ['instagram', 'tiktok', 'youtube'];
    const mappedChannelsMetrics = shownChannels
      .reduce<Array<MetricsChannelsBasicItem>>((result, channel) => {
        const channelInfo = subcampaign.creatorProfile.channels[channel];
        if (channelInfo) {
          result.push({
            engagementRate: channelInfo.metrics?.engagement,
            followers: channelInfo.metrics?.followers,
            type: channel,
            url: channelInfo.url
          });
        }

        return result;
      }, []);

    return (
      <div className="messenger-messages-header__channels">
        {
          mappedChannelsMetrics.map(channel => (
            <div
              key={channel.type}
              className="messenger-messages-header__channel-info"
            >
              <div className="messenger-messages-header__channel-metric">
                <MetricsChannelsBasicSingleEntry
                  item={channel}
                  showChannels={['instagram', 'tiktok', 'youtube']}
                  layout="block"
                  includeRatio
                />
              </div>
              <CreatorTilePriceReco
                className="messenger-messages-header__price-reco"
                caption={__t('messenger_messages_header.price_reco_label')}
                captionClassName="messenger-messages-header__price-reco-caption"
                channelsData={subcampaign.creatorProfile.channels}
                channels={[channel.type]}
              />
            </div>
          ))
        }
      </div>
    );
  }

  renderInfluencerHeader(): React.ReactNode {
    const { subcampaign, offerDeliverables, variablePaymentsAmount } = this.props;
    const entity = getEntityFromProfile(subcampaign.creatorProfile);

    return (
      <div className="messenger-messages-header" data-hj-suppress>
        <div className="messenger-messages-header__content">
          <div className="messenger-messages-header__content-block messenger-messages-header__content-block--centered">
            <div className="messenger-messages-header__avatar">
              <EntityAvatar
                bordered
                size="bigger"
                entity={entity}
                link={entity?.url}
                openInNewTab
              />
            </div>
          </div>

          <div className="messenger-messages-header__summary">
            <span className="messenger-messages-header__name text-overflow-ellipsis">
              <span
                className="messenger-messages-header__artist-name"
                data-qa="messenger-messages-header__artist-name"
              >
                {`${entity?.displayName} `}
              </span>
              {`(${getFullName(entity)})`}
            </span>
            <div className="messenger-messages-header__content-block">
              {this.renderChannels()}
            </div>
          </div>

          {(offerDeliverables && offerDeliverables.length > 0) && (
            <React.Fragment>
              <HeaderSeparator />

              <div className="messenger-messages-header__content-block messenger-messages-header__deliverables">
                <div className="messenger-messages-header__deliverables-count">
                  Tasks: {offerDeliverables.length}
                </div>
                <DeliverableTypeCounters deliverables={offerDeliverables} />
              </div>
            </React.Fragment>
          )}

          <HeaderSeparator />

          <div className="messenger-messages-header__content-block">
            <MessengerSubcampaignState
              subcampaign={subcampaign}
              variablePaymentsAmount={variablePaymentsAmount}
            />
          </div>
        </div>
      </div>
    );
  }

  renderContextMenuForInfluencer() {
    const items = [
      {
        name: 'mark-as-unread',
        text: __t('messenger_messages_header.mark_as_unread_label'),
        onClick: this.handleMarkConversationAsUnread
      }
    ];

    return <MoreButtonWithMenu width={20} contextMenuProps={{ items }} />;
  }

  renderBrandHeader(): React.ReactNode {
    const { brandProfile } = this.props;

    if (!brandProfile || !brandProfile.id) {
      return null;
    }

    const managedByBrand = getManagedByBrand(brandProfile);
    const entity = getEntityFromProfile(brandProfile);

    return (
      <div className="messenger-messages-header">
        <div className="messenger-messages-header__content">
          <div className="messenger-messages-header__content-block messenger-messages-header__content-block--centered">
            <div className="messenger-messages-header__avatar">
              <EntityAvatar
                bordered
                size="bigger"
                entity={entity}
                link={entity?.url}
                openInNewTab
              />
            </div>

            <div className="messenger-messages-header__summary">
              <span className="messenger-messages-header__name  text-overflow-ellipsis">
                <strong className="messenger-messages-header__artist-name">
                  {`${entity?.displayName} `}
                </strong>
              </span>
              {managedByBrand ?
                <ManagedByTag managedByBrand={managedByBrand} />
                : null}
            </div>
          </div>
          <div>
            {this.renderContextMenuForInfluencer()}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { subcampaign, subcampaignId, profileType } = this.props;

    if (isNil(subcampaign) || isNil(subcampaign.id) || subcampaign.id !== subcampaignId) {
      return <div className="messenger-messages-header" />;
    }

    if (profileType === ORGANIZATION_PROFILE_TYPE_BRAND) {
      return this.renderInfluencerHeader();
    }

    return this.renderBrandHeader();
  }
}

const mapStateToProps = ({
  account,
  subcampaign,
  brandProfile,
  organizations,
  offerDeliverables,
  runtimeConfig
}): StateProps => ({
  subcampaign: subcampaign.data,
  brandProfile: brandProfile.profile,
  currentOrganizationId: organizations.currentOrganizationId,
  offerDeliverables: (subcampaign.data && subcampaign.data.offer && offerDeliverables.deliverablesByOfferId[subcampaign.data.offer.id]) || [],
  disableUnreadMessages: isDisableUnreadMessagesSelector({ runtimeConfig, account })
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchMarkConversationAsUnread: (conversationExternalId) => dispatch(markConversationAsUnread(conversationExternalId)),
  dispatchFetchUnreadConversations: (organizationId) => dispatch(fetchUnreadConversations(organizationId))
});

export default connect(mapStateToProps, mapDispatchToProps)(MessengerMessagesHeader);
